import type { BlogArticle } from '@/lib/blog-data'
import { PUBLIC_SOUND_COUNT_LABEL } from '@/lib/public-sound-count'

export const post: BlogArticle = {
    slug: 'tesla-unlock-sound-how-to-set-up',
    title: "Tesla Unlock Sound: What You Can (and Can't) Customize",
    excerpt: 'Want a custom Tesla unlock sound? Here is the honest answer: Tesla plays LockChime.wav on lock only. No separate unlock file exists.',
    updatedAt: '2026-06-01',
  image: '/images/blog/generated/tesla-unlock-sound-how-to-set-up.png',
    content: `
# Tesla Unlock Sound: What You Can (and Can't) Customize

Searching for how to set a custom Tesla unlock sound? Here is the straight answer: **there is no separate unlock sound file**. Tesla plays \`LockChime.wav\` when you lock the car. That is the one customizable audio event. We have found no \`UnlockChime.wav\` file or unlock sound setting from Tesla.

An earlier version of this article claimed Tesla firmware 2026.8 added separate unlock sound support. That claim was not supported by the official 2026.8 or 2026.14 release notes, and we retracted it. We have found no source for a custom sound that plays on unlock.

That said, the lock sound is genuinely great to customize, and this guide covers exactly how.

## What Actually Plays When You Lock vs. Unlock

When you lock your Tesla:
- Your custom \`LockChime.wav\` plays through the external pedestrian-warning speaker (if set)
- If no custom file is loaded, the factory two-tone confirmation plays

When you unlock your Tesla:
- The factory confirmation sound plays (or silence, depending on your settings)
- Your custom \`LockChime.wav\` does **not** play on unlock in standard firmware

There is no UI option to set a separate file for the unlock event. One file, one event: lock.

## Which Tesla Models Support Custom Lock Sounds?

The custom lock sound feature requires an external pedestrian-warning speaker. It works on:

- **Model 3**: Built September 2019 or later
- **Model Y**: All model years
- **Model S**: The 2021 refresh or later
- **Model X**: The 2021 refresh or later
- **Cybertruck**: All

The feature has been available since Tesla's 2023 Holiday Update (software 2023.44). If you do not see the Lock Sound option, go to Controls > Software and update.

## How to Set a Custom Lock Sound

**What you need:** a USB drive formatted FAT32 or exFAT, and a \`LockChime.wav\` file.

**File setup:**

\`\`\`
USB Drive (FAT32 or exFAT)
+-- Boombox/
    +-- LockChime.wav
\`\`\`

The folder must be named exactly **Boombox**. The file must be named exactly **LockChime.wav**.

**Recommended audio specs** (best-practice for clean playback, not official Tesla requirements):
- Format: WAV, 16-bit PCM
- Sample rate: 44.1 kHz
- Duration: 1 to 5 seconds
- File size: under 1 MB

Every sound in our library downloads ready to go, already named \`LockChime.wav\` and formatted correctly.

**Install steps:**

1. Download your chosen sound from [the library](/sounds)
2. Copy it to the \`Boombox\` folder on a FAT32 USB drive
3. Plug the USB into a USB port that carries data (the glovebox port on newer cars)
4. Go to **Controls > Toybox > Boombox > Lock Sound** and select your file
5. Lock your car to hear it play

Full walkthrough: [Tesla Lock Sound Installation Guide](/guide)

## Frequently Asked Questions

### Is there really no unlock sound file?

Correct, as far as we can find. The [2026.8](https://www.notateslaapp.com/software-updates/version/2026.8/release-notes) and [2026.14](https://www.notateslaapp.com/software-updates/version/2026.14/release-notes) release notes list no unlock sound feature, and we know of no \`UnlockChime.wav\` setting. Your \`LockChime.wav\` plays when you lock. The factory confirmation tone handles unlock.

### What about Walk-Away Lock?

Walk-Away Lock locks the car automatically as you walk away. Your custom \`LockChime.wav\` plays at the moment of lock, whether triggered manually or by Walk-Away Lock. There is still no separate unlock file.

### Can I silence the unlock tone but keep a custom lock sound?

The Lock Confirmation Sound toggle in Controls > Locks controls the lock confirmation sound. Tesla does not document a separate unlock-sound control. If you want a quieter lock, load a short, low-volume \`LockChime.wav\` instead of toggling the feature off.

### What format does the WAV file need to be?

The only hard Tesla requirement is a WAV file named exactly \`LockChime.wav\`. For best playback quality, use 44.1 kHz, 16-bit PCM WAV, under 1 MB. These are recommended specs, not official Tesla requirements, but they work reliably across all models.

---

Browse ${PUBLIC_SOUND_COUNT_LABEL} free lock sounds: [Sound Library](/sounds) | [Installation Guide](/guide)
    `,
    author: 'Tesla Lock Sound Team',
    publishedAt: '2026-04-12',
    category: 'tutorials',
    tags: ['unlock-sound', 'LockChime', 'firmware', 'setup'],
    readingTime: 4,
    featured: true,
    soundCategory: 'Movies & TV',
    soundLimit: 6
  }
